import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { userRequest } from "../utils/requestMethods";
import { Header, BackButton } from "../components";
import GuardianModal from "../components/GuardianModal";
import useWriteGuard from "../hooks/useWriteGuard";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiLink,
  FiPlus,
  FiShield,
  FiUser,
  FiUsers,
} from "react-icons/fi";

const inputClass =
  "w-full px-3.5 py-2.5 text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 focus:bg-white placeholder-gray-400 transition-all";
const disabledInputClass = `${inputClass} opacity-60 cursor-not-allowed`;

const Field = ({ label, optional, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
      {label}{" "}
      {optional && <span className="normal-case font-normal text-gray-400">(Optional)</span>}
    </label>
    {children}
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, color = "indigo" }) => (
  <div
    className={`flex items-center gap-3 p-3 rounded-xl border ${color === "violet" ? "bg-violet-50 border-violet-100" : "bg-indigo-50 border-indigo-100"}`}
  >
    <div
      className={`w-8 h-8 rounded-lg flex items-center justify-center bg-white/70 ${color === "violet" ? "text-violet-600" : "text-indigo-600"}`}
    >
      <Icon size={15} />
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-800">{title}</p>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </div>
  </div>
);

const emptyGuardian = () => ({
  id: "",
  firstname: "",
  lastname: "",
  phone: "",
  email: "",
  phoneVerified: false,
  emailVerified: false,
});

const UpdateStudent = ({ history }) => {
  const location = useLocation();
  const childId = location.pathname.split("/")[2];
  const { isWriteBlocked, guardWrite } = useWriteGuard();
  const [student, setStudent] = useState({
    firstname: "",
    lastname: "",
    middlename: "",
    gender: "",
    registrationId: "",
  });
  const [guardians, setGuardians] = useState([emptyGuardian(), emptyGuardian()]);
  const [schoolId, setSchoolId] = useState("");
  const [showParent2, setShowParent2] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [guardianModal, setGuardianModal] = useState(null);
  const [parentSearch, setParentSearch] = useState("");
  const [parentResults, setParentResults] = useState([]);
  const [parentSearchLoading, setParentSearchLoading] = useState(false);
  const [guardianSaving, setGuardianSaving] = useState(false);
  const [newParent, setNewParent] = useState({ firstname: "", lastname: "", phone: "", email: "" });

  const showMessage = (setter, message) => {
    setter(message);
    window.setTimeout(() => setter(""), 5000);
  };

  useEffect(() => {
    const fetchChild = async () => {
      try {
        setFetchLoading(true);
        const { data } = await userRequest.get(`children/${childId}`);
        setStudent({
          firstname: data.firstname || "",
          lastname: data.lastname || "",
          middlename: data.middlename || "",
          gender: data.gender || "",
          registrationId: data.registration_id || "",
        });
        setSchoolId(String(data.school_id?._id || data.school_id || ""));
        const linked = Array.isArray(data.guardians)
          ? data.guardians
          : data.user
            ? [data.user]
            : [];
        setGuardians(
          [0, 1].map((index) => {
            const parent = linked[index];
            return parent
              ? {
                  id: String(parent._id || parent.id || ""),
                  firstname: parent.firstname || "",
                  lastname: parent.lastname || "",
                  phone: parent.mobile_number || "",
                  email: parent.email || "",
                  phoneVerified: !!parent.is_phone_verified,
                  emailVerified: !!parent.is_email_verified,
                }
              : emptyGuardian();
          }),
        );
        setShowParent2(linked.length > 1);
      } catch (err) {
        showMessage(setError, err.response?.data || "Failed to load student details.");
      } finally {
        setFetchLoading(false);
      }
    };
    fetchChild();
  }, [childId]);

  useEffect(() => {
    if (!guardianModal || guardianModal.mode !== "link" || !schoolId) return undefined;
    const timer = window.setTimeout(async () => {
      try {
        setParentSearchLoading(true);
        const query = new URLSearchParams({
          limit: "1000",
          search: parentSearch.trim(),
        }).toString();
        const { data } = await userRequest.get(`/schools/${schoolId}/parents?${query}`);
        setParentResults(
          Array.isArray(data?.parents) ? data.parents : Array.isArray(data) ? data : [],
        );
      } catch (err) {
        setParentResults([]);
        showMessage(setError, err.response?.data || "Could not search parent accounts.");
      } finally {
        setParentSearchLoading(false);
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [childId, guardianModal, parentSearch, schoolId]);

  const updateGuardian = (slot, field, value) =>
    setGuardians((current) =>
      current.map((guardian, index) =>
        index === slot ? { ...guardian, [field]: value } : guardian,
      ),
    );
  const openLink = (slot) => {
    setParentSearch("");
    setParentResults([]);
    setGuardianModal({ slot, mode: "link" });
  };
  const openCreate = (slot) => {
    setNewParent({ firstname: "", lastname: "", phone: "", email: "" });
    setGuardianModal({ slot, mode: "create" });
  };
  const closeGuardianModal = () => {
    if (!guardianSaving) setGuardianModal(null);
  };

  const linkParent = async (parent) => {
    if (!guardWrite()) return;
    setGuardianSaving(true);
    try {
      const effectiveSlot = guardianModal.slot === 1 && !guardians[0].id ? 0 : guardianModal.slot;
      await userRequest.post(`/children/${childId}/guardians/link`, {
        slot: effectiveSlot,
        parent_id: parent._id,
      });
      setGuardians((current) =>
        current.map((guardian, index) =>
          index === effectiveSlot
            ? {
                ...guardian,
                id: parent._id,
                firstname: parent.firstname || "",
                lastname: parent.lastname || "",
                phone: parent.mobile_number || "",
                email: parent.email || "",
                phoneVerified: !!parent.is_phone_verified,
                emailVerified: !!parent.is_email_verified,
              }
            : guardian,
        ),
      );
      setShowParent2(effectiveSlot === 1 || showParent2);
      setGuardianModal(null);
      showMessage(
        setSuccess,
        `${parent.firstname || ""} ${parent.lastname || ""}`.trim() + " linked successfully.",
      );
    } catch (err) {
      showMessage(setError, err.response?.data || "Could not link this parent.");
    } finally {
      setGuardianSaving(false);
    }
  };

  const createParent = async (event) => {
    event.preventDefault();
    if (!guardWrite()) return;
    setGuardianSaving(true);
    try {
      const effectiveSlot = guardianModal.slot === 1 && !guardians[0].id ? 0 : guardianModal.slot;
      const { data } = await userRequest.post(`/children/${childId}/guardians/create`, {
        slot: effectiveSlot,
        ...newParent,
      });
      const linkedId = data.guardians?.[effectiveSlot] || data.user || "";
      setGuardians((current) =>
        current.map((guardian, index) =>
          index === effectiveSlot ? { ...guardian, id: String(linkedId), ...newParent } : guardian,
        ),
      );
      setShowParent2(effectiveSlot === 1 || showParent2);
      setGuardianModal(null);
      showMessage(setSuccess, "Parent account created and linked successfully.");
    } catch (err) {
      showMessage(setError, err.response?.data || "Could not create this parent account.");
    } finally {
      setGuardianSaving(false);
    }
  };

  const submitHandler = async (event) => {
    event.preventDefault();
    if (!guardWrite()) return;
    setLoading(true);
    try {
      const payload = {
        firstname: student.firstname,
        lastname: student.lastname,
        gender: student.gender,
        registration_id: student.registrationId,
      };
      if (student.middlename.trim()) payload.middlename = student.middlename;
      guardians.forEach((parent, index) => {
        if (!parent.id && !parent.firstname && !parent.lastname && !parent.phone && !parent.email)
          return;
        payload[`parent${index + 1}`] = {
          firstname: parent.firstname,
          lastname: parent.lastname,
          ...(parent.phone ? { phone: parent.phone } : {}),
          ...(parent.email ? { email: parent.email } : {}),
        };
      });
      await userRequest.put(`/children/${childId}`, payload);
      showMessage(setSuccess, "Student details updated successfully.");
    } catch (err) {
      showMessage(setError, err.response?.data || "Failed to update student details.");
    } finally {
      setLoading(false);
    }
  };

  const renderGuardianFields = (slot, parent, locked = false, requiredFields = false) => (
    <div className="grid grid-cols-2 gap-3">
      <Field label="First Name" optional={!requiredFields}>
        <input
          required={requiredFields}
          value={parent.firstname}
          onChange={(e) => updateGuardian(slot, "firstname", e.target.value)}
          className={inputClass}
        />
      </Field>
      <Field label="Last Name" optional={!requiredFields}>
        <input
          required={requiredFields}
          value={parent.lastname}
          onChange={(e) => updateGuardian(slot, "lastname", e.target.value)}
          className={inputClass}
        />
      </Field>
      <Field label="Phone Number" optional={!requiredFields}>
        <input
          required={requiredFields}
          value={parent.phone}
          disabled={locked || parent.phoneVerified}
          onChange={(e) => updateGuardian(slot, "phone", e.target.value)}
          className={locked || parent.phoneVerified ? disabledInputClass : inputClass}
          title={
            parent.phoneVerified
              ? "Verified phone numbers require account verification to change."
              : ""
          }
        />
      </Field>
      <Field label="Email Address" optional>
        <input
          type="email"
          value={parent.email}
          disabled={locked || parent.emailVerified}
          onChange={(e) => updateGuardian(slot, "email", e.target.value)}
          className={locked || parent.emailVerified ? disabledInputClass : inputClass}
          title={
            parent.emailVerified
              ? "Verified email addresses require account verification to change."
              : ""
          }
        />
      </Field>
    </div>
  );

  const renderGuardian = (slot, color, title, subtitle) => {
    const parent = guardians[slot];
    const isLinked = !!parent.id;
    const contactLocked = parent.phoneVerified || parent.emailVerified;
    return (
      <section className="space-y-4">
        <SectionHeader icon={FiUsers} title={title} subtitle={subtitle} color={color} />
        {!isLinked ? (
          <div className="p-5 rounded-2xl border border-dashed border-gray-300 bg-gray-50">
              {renderGuardianFields(slot, parent, false, true)}
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                type="button"
                onClick={() => openLink(slot)}
                className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100"
              >
                <FiLink size={14} /> Link existing parent
              </button>
            </div>
          </div>
        ) : (
          <>
            {renderGuardianFields(slot, parent)}
            {contactLocked && (
              <p className="text-xs text-gray-500">
                <FiShield size={13} className="inline mr-1" /> Verified phone or email cannot be
                changed here. Update it from the parent’s account so the new contact can be verified
                securely.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => openLink(slot)}
                className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100"
              >
                <FiLink size={14} /> Replace with existing parent
              </button>
              <button
                type="button"
                onClick={() => openCreate(slot)}
                className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50"
              >
                <FiPlus size={14} /> Replace with new parent
              </button>
            </div>
          </>
        )}
      </section>
    );
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 bg-white rounded-3xl min-h-screen">
      <BackButton history={history} />
      <Header category="Students" title="Update Student Record" />
      {fetchLoading ? (
        <div className="flex justify-center items-center h-48">
          <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
        </div>
      ) : (
        <div className="mt-6">
          {error && (
            <div className="flex items-center gap-3 p-4 mb-5 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <FiAlertCircle size={16} />
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-3 p-4 mb-5 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
              <FiCheckCircle size={16} />
              {success}
            </div>
          )}
          <form onSubmit={submitHandler} className="space-y-6">
            <SectionHeader
              icon={FiUser}
              title="Student Information"
              subtitle="Basic identity details for this student"
            />
            <div className="grid grid-cols-3 gap-3">
              <Field label="First Name">
                <input
                  required
                  value={student.firstname}
                  onChange={(e) => setStudent({ ...student, firstname: e.target.value })}
                  className={inputClass}
                />
              </Field>
              <Field label="Middle Name" optional>
                <input
                  value={student.middlename}
                  onChange={(e) => setStudent({ ...student, middlename: e.target.value })}
                  className={inputClass}
                />
              </Field>
              <Field label="Last Name">
                <input
                  required
                  value={student.lastname}
                  onChange={(e) => setStudent({ ...student, lastname: e.target.value })}
                  className={inputClass}
                />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Gender">
                <select
                  required
                  value={student.gender}
                  onChange={(e) => setStudent({ ...student, gender: e.target.value })}
                  className={inputClass}
                >
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </Field>
              <Field label="Admission Number">
                <input readOnly value={student.registrationId} className={disabledInputClass} />
              </Field>
            </div>
            <div className="border-t border-gray-100" />
            {renderGuardian(
              0,
              "indigo",
              "Primary Parent / Guardian",
              "Main contact person for this student",
            )}
            {!showParent2 ? (
              <button
                type="button"
                onClick={() => setShowParent2(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100"
              >
                <FiPlus size={14} /> Add Second Parent / Guardian
              </button>
            ) : (
              renderGuardian(
                1,
                "violet",
                "Secondary Parent / Guardian",
                "Additional contact for this student",
              )
            )}
            <div className="flex justify-end pt-2 border-t border-gray-100">
              <button
                type="submit"
                disabled={loading || isWriteBlocked}
                className="px-8 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 rounded-xl"
              >
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </div>
      )}
      <GuardianModal
        modal={guardianModal}
        close={closeGuardianModal}
        search={parentSearch}
        setSearch={setParentSearch}
        searchLoading={parentSearchLoading}
        results={parentResults}
        saving={guardianSaving}
        onLink={linkParent}
        onOpenCreate={openCreate}
        newParent={newParent}
        setNewParent={setNewParent}
        onCreate={createParent}
      />
    </div>
  );
};

export default UpdateStudent;
